import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Smartphone, X } from "lucide-react";

/**
 * <InstallAppPrompt /> — a bottom-sheet install nudge.
 *
 * Feb 2026 retention tuning:
 *   • NEVER show on the first visit — kids need to see the app first.
 *   • Show once at the START of the 2nd visit (and every N-th visit
 *     after a dismissal cooldown).
 *   • If the child/parent dismisses it, hide for 14 days.
 *   • Bottom-sheet placement — sits ABOVE the fixed mobile tab bar so
 *     it never covers tappable content.
 *
 * "Visit" is tracked in localStorage (`mwq_visit_count`); we bump it
 * once per tab session via a sessionStorage flag so a page reload
 * doesn't double-count.
 *
 * iOS Safari doesn't fire `beforeinstallprompt`, so on iOS we show a
 * short "Share → Add to Home Screen" hint using the same sheet.
 */

const DISMISS_KEY = "mwq_install_prompt_dismissed_at";
const SHOWN_KEY = "mwq_install_prompt_shown_at";
const VISITS_KEY = "mwq_visit_count";
const SESSION_FLAG = "mwq_visit_counted_this_session";
const DISMISS_COOLDOWN_DAYS = 14;
const SHOWN_COOLDOWN_MS = 24 * 60 * 60 * 1000;  // once per day, hard cap

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const last = parseInt(v, 10);
    if (!Number.isFinite(last)) return false;
    const days = (Date.now() - last) / (1000 * 60 * 60 * 24);
    return days < DISMISS_COOLDOWN_DAYS;
  } catch {
    return false;
  }
}

/**
 * Hard "at most once per day" gate — independent of whether the child
 * dismissed the sheet. Fixes the Feb 2026 report: the popup was
 * appearing on every visit because kids typically CLOSE THE TAB rather
 * than tap the X, so the DISMISS_KEY never gets stamped and we never
 * cooled down. Stamping SHOWN_KEY every time we show the sheet caps it
 * at once per 24 hours no matter what.
 */
function shownRecently() {
  try {
    const v = localStorage.getItem(SHOWN_KEY);
    if (!v) return false;
    const last = parseInt(v, 10);
    if (!Number.isFinite(last)) return false;
    return Date.now() - last < SHOWN_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markShown() {
  try { localStorage.setItem(SHOWN_KEY, String(Date.now())); } catch { /* ignore */ }
}

// Bump the visit counter once per browser session and return the new
// total. We use sessionStorage as a guard so a page reload inside the
// same tab doesn't inflate the count.
function bumpVisitCount() {
  try {
    if (sessionStorage.getItem(SESSION_FLAG)) {
      return parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) || 0;
    }
    const prev = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) || 0;
    const next = prev + 1;
    localStorage.setItem(VISITS_KEY, String(next));
    sessionStorage.setItem(SESSION_FLAG, "1");
    return next;
  } catch {
    return 1;
  }
}

export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (recentlyDismissed()) return undefined;
    // Hard cap: at most once per 24 hours regardless of whether the
    // child ever tapped X. Kids close tabs; without this cap the sheet
    // reappeared on every browser session.
    if (shownRecently()) return undefined;

    // Retention rule: only show from the 2nd visit onwards.
    const visits = bumpVisitCount();
    if (visits < 2) return undefined;

    // Android / Chromium: capture install event so we can fire it on demand.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      // Chrome can fire this multiple times per navigation — guard so
      // we don't re-show and re-stamp within the same session.
      if (shownRecently()) return;
      setDeferred(e);
      setShow(true);
      markShown();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: no install event — show the hint after a short delay so it
    // doesn't interrupt the very first thing kids see.
    let iosTimer;
    if (isIos()) {
      iosTimer = window.setTimeout(() => {
        if (shownRecently()) return;
        setIosHint(true);
        setShow(true);
        markShown();
      }, 6000);
    }

    // Clean up on PWA install (Chromium fires this) so the sheet vanishes.
    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const triggerInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch {
      /* user cancelled */
    } finally {
      setDeferred(null);
      setShow(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          role="dialog"
          aria-label="Install My World Quest"
          data-testid="install-prompt"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          // Bottom-sheet: pinned to the bottom edge but ABOVE the
          // 72-px mobile tab bar so it never covers navigation. Uses
          // safe-area-inset so it clears iPhone home indicators.
          className="fixed z-40 left-2 right-2 max-h-[70px] rounded-2xl border-[3px] border-slate-900 bg-yellow-300 shadow-[0_-6px_18px_-4px_rgba(15,23,42,0.28)] px-3 py-2"
          style={{ bottom: "calc(80px + env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-2">
            <Smartphone size={18} strokeWidth={3} aria-hidden className="shrink-0" />
            <p className="min-w-0 flex-1 text-sm font-bold leading-tight truncate" data-testid="install-prompt-text">
              {iosHint ? <>Add MWQ to Home Screen · <span className="font-normal">Share → Add to Home Screen</span></> : "Add MWQ to Home Screen"}
            </p>
            {!iosHint && deferred && (
              <button type="button" onClick={triggerInstall} data-testid="install-prompt-confirm" className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-[3px] border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] shrink-0">
                <Download size={13} strokeWidth={3} /> Add
              </button>
            )}
            <button type="button" onClick={dismiss} data-testid="install-prompt-dismiss" aria-label="Dismiss install prompt" className="h-9 w-9 inline-flex items-center justify-center rounded-full border-[2.5px] border-slate-900 bg-white shrink-0">
              <X size={14} strokeWidth={3} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
